// src/hooks/useEquipment.js

import { useState, useEffect, useCallback } from 'react';
import equipmentApi from '../api/equipmentApi';

export const useEquipment = (programId, filters = {}) => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [availableSponsors, setAvailableSponsors] = useState([]);
  
  const fetchEquipment = useCallback(async () => {
    if (!programId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get program to find sport and clubId
      const programResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
      );
      const program = await programResponse.json();
      
      if (!program || !program.sport) {
        throw new Error('Failed to fetch equipment - no program sport found');
      }
      
      // Extract clubId
      const programClubId = program.clubId?._id || program.clubId;
      
      let equipmentList = [];
      let usedFallback = false;
      
      // Try new filtered endpoint first (supports club/program scoping)
      try {
        const data = await equipmentApi.getEquipment(programId, filters);
        equipmentList = Array.isArray(data) ? data : (data.equipments || data.equipment || []);
        
        // If new endpoint returns 0, try old endpoint as fallback
        if (equipmentList.length === 0) {
          usedFallback = true;
          throw new Error('Empty result, use fallback');
        }
      } catch (filteredApiError) {
        // Fallback to old sport-based endpoint
        try {
          const data = await equipmentApi.getBySport(program.sport);
          let allEquipment = Array.isArray(data) ? data : (data.equipments || data.equipment || []);
          
          // IMPORTANT: Filter by club on client-side for security
          equipmentList = allEquipment.filter(eq => {
            // Check if equipment belongs to this club
            const equipmentClubId = eq.scope?.clubId || eq.clubId;
            
            // If no club info, include it (old legacy data - assume it's for all clubs)
            if (!equipmentClubId) return true;
            
            // Only include if clubId matches this program's club
            return String(equipmentClubId) === String(programClubId);
          });
        } catch (sportApiError) {
          console.error('Both API endpoints failed');
          throw new Error('Unable to fetch equipment. Please contact administrator.');
        }
      }
      
      // Additional filter: Ensure equipment matches program's sport
      equipmentList = equipmentList.filter(eq => {
        const equipmentSport = eq.sportName || eq.sport;
        return !equipmentSport || equipmentSport === program.sport;
      });
      
      // Apply client-side filtering if filters are set
      if (filters.category) {
        equipmentList = equipmentList.filter(eq => {
          // Handle both old (string) and new (ObjectId) category formats
          const categoryId = eq.category?._id || eq.category;
          const categoryName = eq.category?.name || eq.category || eq.categoryName;
          // Match by ID or by name (backward compatibility)
          return categoryId === filters.category || categoryName === filters.category;
        });
      }
      
      if (filters.minRating) {
        const minRating = parseFloat(filters.minRating);
        equipmentList = equipmentList.filter(eq => 
          eq.ratingSummary && eq.ratingSummary.averageRating >= minRating
        );
      }
      
      if (filters.sponsor) {
        equipmentList = equipmentList.filter(eq => 
          eq.sponsorInfo?.name === filters.sponsor
        );
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        equipmentList = equipmentList.filter(eq => 
          eq.equipmentName?.toLowerCase().includes(searchLower) ||
          eq.description?.toLowerCase().includes(searchLower) ||
          eq.sponsorInfo?.name?.toLowerCase().includes(searchLower)
        );
      }
      
      // Apply sorting
      if (filters.sort === 'rating') {
        equipmentList = [...equipmentList].sort((a, b) => 
          (b.ratingSummary?.averageRating || 0) - (a.ratingSummary?.averageRating || 0)
        );
      } else if (filters.sort === 'name') {
        equipmentList = [...equipmentList].sort((a, b) => 
          (a.equipmentName || '').localeCompare(b.equipmentName || '')
        );
      } else {
        // Default: newest first (if createdAt exists)
        equipmentList = [...equipmentList].sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
      }
      
      setEquipment(equipmentList);
      setStats(null);
      setPagination(null);
      
      // Extract unique sponsors from equipment list
      const sponsors = [...new Set(
        equipmentList
          .filter(eq => eq.sponsorInfo?.name)
          .map(eq => eq.sponsorInfo.name)
      )].sort();
      
      setAvailableSponsors(sponsors);
      
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError(err.message);
      setEquipment([]);
    } finally {
      setLoading(false);
    }
  }, [programId, JSON.stringify(filters)]);
  
  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);
  
  const refresh = useCallback(() => {
    fetchEquipment();
  }, [fetchEquipment]);
  
  return { 
    equipment, 
    loading, 
    error, 
    stats,
    pagination,
    availableSponsors,
    refresh 
  };
};

export default useEquipment;

